import { ShowroomBranding } from "@/components/dealer/ShowroomBranding";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Mail, Phone, UserRound, BadgeCheck, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const INPUT = "h-11 bg-white focus-visible:ring-terracotta";

function Field({ label, children }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="text-xs uppercase tracking-wide text-warmgrey">{label}</span>
      {children}
    </label>
  );
}

function AddressLines({ a }) {
  if (!a) return <span className="text-warmgrey">No address on file</span>;
  return <span>{[a.line1, a.line2, [a.city, a.state].filter(Boolean).join(", "), a.postal_code].filter(Boolean).join(" · ")}</span>;
}

export default function DealerProfile() {
  const [params] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ name: "", job_title: "", phone: "", phone_ext: "" });
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [editLoc, setEditLoc] = useState(null);

  const [syncKey, setSyncKey] = useState(0);
  const bump = () => { setSyncKey((k) => k + 1); window.dispatchEvent(new Event("dealer:profile-changed")); };
  const load = async () => {
    const [p, l] = await Promise.all([api.get("/dealer/profile/me"), api.get("/dealer/locations")]);
    setProfile(p.data);
    setLocations(l.data);
    setForm({ name: p.data.name, job_title: p.data.job_title, phone: p.data.phone, phone_ext: p.data.phone_ext });
  };
  const fail = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  useEffect(() => {
    load().catch(fail);
    const token = params.get("verify");
    if (token) {
      api.post("/dealer/profile/me/email-change/verify", { token })
        .then((r) => { toast.success(`Business e-mail verified: ${r.data.email}`); return load(); })
        .catch(fail);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { setProfile((await api.patch("/dealer/profile/me", form)).data); toast.success("Profile updated"); bump(); } catch (err) { fail(err); } finally { setSaving(false); }
  };
  const requestEmail = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/dealer/profile/me/email-change", { new_email: newEmail });
      toast.success(`Verification sent to ${r.data.new_email} — your login changes only after you confirm it`);
      setNewEmail(""); await load();
    } catch (err) { fail(err); }
  };
  const selectShowroom = async (location_id) => {
    try { setProfile((await api.patch("/dealer/profile/me/showroom", { location_id })).data); toast.success("Primary showroom updated"); bump(); } catch (err) { fail(err); }
  };
  const saveLocation = async (e) => {
    e.preventDefault();
    const { id, ...raw } = editLoc;
    const body = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, v === "" ? null : v]));
    try { await api.patch(`/dealer/locations/${id}`, body); toast.success("Showroom details updated"); setEditLoc(null); await load(); bump(); } catch (err) { fail(err); }
  };

  if (!profile) return <div className="p-6 text-sm text-warmgrey" data-testid="profile-loading">Loading profile…</div>;
  const canEditLoc = (loc) => profile.can_edit_locations || (profile.can_edit_own_location && loc.id === profile.assigned_location?.id);

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-4 sm:p-6" data-testid="dealer-profile-page">
      <header>
        <h1 className="font-display text-2xl text-charcoal sm:text-3xl">My Profile</h1>
        <p className="text-sm text-warmgrey">Business identity and showroom context used across Zelligio Connect and ZELI. Business information only.</p>
      </header>

      <section className="rounded-lg border border-sand bg-white p-4 sm:p-6" data-testid="profile-identity">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <BadgeCheck className="h-4 w-4 text-terracotta" aria-hidden="true" />
          <span className="font-medium" data-testid="profile-role">{profile.role_label}</span>
          <span className="text-warmgrey">· governed role — changes are authorized and audited by a Dealer Admin</span>
        </div>
        <form onSubmit={saveProfile} className="grid gap-4 sm:grid-cols-2">
          <Field label="Employee full name"><Input data-testid="profile-name" className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="Job title"><Input data-testid="profile-job-title" className={INPUT} value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></Field>
          <Field label="Business phone"><Input data-testid="profile-phone" className={INPUT} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="305-555-0100" /></Field>
          <Field label="Extension (optional)"><Input data-testid="profile-phone-ext" className={INPUT} value={form.phone_ext} onChange={(e) => setForm({ ...form, phone_ext: e.target.value })} placeholder="204" /></Field>
          <div className="sm:col-span-2"><Button type="submit" disabled={saving} data-testid="profile-save" className="h-11 bg-terracotta text-white hover:bg-terracotta-hover">Save business details</Button></div>
        </form>
      </section>

      <ShowroomBranding refreshKey={syncKey} onSaved={async () => { await load(); bump(); }} />

      <section className="rounded-lg border border-sand bg-white p-4 sm:p-6" data-testid="profile-email">
        <h2 className="mb-2 flex items-center gap-2 text-base font-medium md:text-lg"><Mail className="h-4 w-4" aria-hidden="true" /> Business e-mail</h2>
        <p className="text-sm" data-testid="profile-email-current">{profile.email}</p>
        {profile.pending_email_change && <p className="mt-1 text-xs text-amber-700" data-testid="profile-email-pending">Pending verification: {profile.pending_email_change} — check that inbox to confirm.</p>}
        <form onSubmit={requestEmail} className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input type="email" data-testid="profile-new-email" className={INPUT} placeholder="new.name@yourcompany.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
          <Button type="submit" variant="outline" data-testid="profile-email-request" className="h-11">Send verification</Button>
        </form>
        <p className="mt-2 text-xs text-warmgrey">Login and notifications switch to the new address only after it is verified.</p>
      </section>

      <section className="rounded-lg border border-sand bg-white p-4 sm:p-6" data-testid="profile-showroom">
        <h2 className="mb-2 flex items-center gap-2 text-base font-medium md:text-lg"><Building2 className="h-4 w-4" aria-hidden="true" /> Assigned showroom</h2>
        {profile.assigned_location ? (
          <div className="text-sm" data-testid="profile-assigned-location">
            <p className="font-medium">{profile.assigned_location.label}</p>
            <p className="text-charcoal"><AddressLines a={profile.assigned_location.address} /></p>
            <p className="text-xs text-warmgrey">Source: {profile.assigned_location_source === "employee_assignment" ? "your assignment" : "dealer default location"} · used as the default ship-to for samples and orders (you can always give another address).</p>
          </div>
        ) : <p className="text-sm text-warmgrey">No showroom on file.</p>}
        {profile.can_choose_showroom && (
          <div className="mt-3">
            <span className="text-xs uppercase tracking-wide text-warmgrey">Select primary showroom (within your authorized locations)</span>
            <div className="mt-1 flex flex-wrap gap-2">
              {locations.map((l) => (
                <Button key={l.id} type="button" variant={l.id === profile.assigned_location?.id ? "default" : "outline"} data-testid={`profile-select-showroom-${l.id}`}
                  className={`h-9 ${l.id === profile.assigned_location?.id ? "bg-terracotta text-white" : ""}`} onClick={() => selectShowroom(l.id)}>{l.label}</Button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-sand bg-white p-4 sm:p-6" data-testid="profile-locations">
        <h2 className="mb-2 flex items-center gap-2 text-base font-medium md:text-lg"><MapPin className="h-4 w-4" aria-hidden="true" /> Showroom / location details</h2>
        <ul className="divide-y divide-sand">
          {locations.map((l) => (
            <li key={l.id} className="py-3 text-sm" data-testid={`location-row-${l.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{l.label}{l.is_default ? <span className="ml-2 rounded bg-sand px-1.5 py-0.5 text-[10px] uppercase text-warmgrey">default</span> : null}</p>
                  <p><AddressLines a={l.address} /></p>
                  <p className="text-xs text-warmgrey"><Phone className="mr-1 inline h-3 w-3" aria-hidden="true" />{l.phone || "—"}{l.phone_ext ? ` ext. ${l.phone_ext}` : ""} · <Mail className="mr-1 inline h-3 w-3" aria-hidden="true" />{l.email || "—"}</p>
                </div>
                {canEditLoc(l) ? (
                  <Button type="button" variant="outline" size="sm" data-testid={`location-edit-${l.id}`} onClick={() => setEditLoc({ id: l.id, label: l.label, phone: l.phone, phone_ext: l.phone_ext, email: l.email, receiving_contact: l.receiving_contact, delivery_instructions: l.delivery_instructions, address: { line1: l.address.line1 || "", line2: l.address.line2 || "", city: l.address.city || "", state: l.address.state || "", postal_code: l.address.postal_code || "", country: l.address.country || "US" } })}>Edit</Button>
                ) : <span className="text-xs text-warmgrey" data-testid={`location-readonly-${l.id}`}>read-only</span>}
              </div>
              {editLoc?.id === l.id && (
                <form onSubmit={saveLocation} className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="location-edit-form">
                  <Field label="Showroom name"><Input data-testid="location-label" className={INPUT} value={editLoc.label} onChange={(e) => setEditLoc({ ...editLoc, label: e.target.value })} required /></Field>
                  <Field label="Showroom business e-mail"><Input type="email" data-testid="location-email" className={INPUT} value={editLoc.email || ""} onChange={(e) => setEditLoc({ ...editLoc, email: e.target.value || null })} /></Field>
                  <Field label="Showroom business phone"><Input data-testid="location-phone" className={INPUT} value={editLoc.phone || ""} onChange={(e) => setEditLoc({ ...editLoc, phone: e.target.value })} /></Field>
                  <Field label="Extension (optional)"><Input data-testid="location-phone-ext" className={INPUT} value={editLoc.phone_ext || ""} onChange={(e) => setEditLoc({ ...editLoc, phone_ext: e.target.value })} /></Field>
                  <Field label="Street address"><Input data-testid="location-line1" className={INPUT} value={editLoc.address.line1} onChange={(e) => setEditLoc({ ...editLoc, address: { ...editLoc.address, line1: e.target.value } })} required /></Field>
                  <Field label="Suite / line 2"><Input data-testid="location-line2" className={INPUT} value={editLoc.address.line2} onChange={(e) => setEditLoc({ ...editLoc, address: { ...editLoc.address, line2: e.target.value } })} /></Field>
                  <Field label="City"><Input data-testid="location-city" className={INPUT} value={editLoc.address.city} onChange={(e) => setEditLoc({ ...editLoc, address: { ...editLoc.address, city: e.target.value } })} required /></Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="State"><Input data-testid="location-state" className={INPUT} value={editLoc.address.state} onChange={(e) => setEditLoc({ ...editLoc, address: { ...editLoc.address, state: e.target.value } })} /></Field>
                    <Field label="ZIP"><Input data-testid="location-postal" className={INPUT} value={editLoc.address.postal_code} onChange={(e) => setEditLoc({ ...editLoc, address: { ...editLoc.address, postal_code: e.target.value } })} /></Field>
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <Button type="submit" data-testid="location-save" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">Save showroom</Button>
                    <Button type="button" variant="ghost" data-testid="location-cancel" className="h-10" onClick={() => setEditLoc(null)}>Cancel</Button>
                  </div>
                </form>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-warmgrey"><UserRound className="mr-1 inline h-3 w-3" aria-hidden="true" />Dealer Admins manage company locations and employee assignments (Team). Location Managers may edit their authorized showroom. Sales Designers have read-only access.</p>
      </section>
    </div>
  );
}
